// UserAnalysis.tsx
import React, { useState } from "react";
import { useColorMode } from "@chakra-ui/react";
import NetSentimentWave from "../ProjectSentiment/Chart/NetSentimentWave";
import { Mode } from "../ProjectSentiment/Chart/useNetSentimentSeries";
import { useUserSentimentSeries } from "../ProjectSentiment/Chart/useUserSentimentSeries";
import { useUserMessages } from "../ProjectSentiment/Chart/useUserMessages";
import WaveLoader from "../../../components/WaveLoader";
import { User } from "../SentimentAnalysis";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import CustomDropdown from "../../../components/CustomDropdown";

const YEARS = Array.from(
  { length: 5 },
  (_, i) => new Date().getFullYear() - 4 + i
);
const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString("default", { month: "short" }),
}));

interface Props {
  selectedUser: string | null;
  users: User[];
}

const UserAnalysis: React.FC<Props> = ({ selectedUser, users }) => {
  const { msgs, isLoading } = useUserMessages(selectedUser);
  const [mode, setMode] = useState<Mode>("today");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [focusSelect, setFocusSelect] = useState<"year" | "month" | null>(null);

  const data = useUserSentimentSeries(selectedUser, mode, { year, month });
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  if (!selectedUser) return null;

  const user = users.find((u) => u.id === selectedUser);
  const userName = user ? `${user.firstName} ${user.lastName}` : "Unknown User";

  // Hooks moved to top


  const positiveMsgs = msgs.filter((m) => m.score > 0);
  const negativeMsgs = msgs.filter((m) => m.score < 0);
  const neutralMsgs = msgs.filter((m) => m.score === 0);

  const totalMsgs = msgs.length;
  const positiveCount = positiveMsgs.length;
  const negativeCount = negativeMsgs.length;
  const neutralCount = neutralMsgs.length;
  const positivePercent = totalMsgs
    ? ((positiveCount / totalMsgs) * 100).toFixed(1)
    : 0;
  const negativePercent = totalMsgs
    ? ((negativeCount / totalMsgs) * 100).toFixed(1)
    : 0;
  const neutralPercent = totalMsgs
    ? ((neutralCount / totalMsgs) * 100).toFixed(1)
    : 0;
  const donutData = [
    { name: "Positive", value: positiveCount },
    { name: "Negative", value: negativeCount },
  ];
  const donutColors = ["#68D391", "#FC8181"];
  const hasNoData =
    positiveCount === 0 && negativeCount === 0 && neutralCount === 0;

  if (isLoading) {
    return (
      <div
        className={`${isDark
          ? "bg-[#1A1C1F] border border-[#2E2F33]"
          : "bg-white border border-gray-200"
          } flex items-center justify-center h-[400px] p-10 rounded-md shadow-sm text-center`}
      >
        <WaveLoader />
      </div>
    );
  }

  if (msgs.length === 0 || hasNoData) {
    return (
      <div
        className={`${isDark
          ? "bg-[#1A1C1F] border border-[#2E2F33]"
          : "bg-white border border-gray-200"
          } flex items-center justify-center h-[400px] p-10 rounded-md shadow-sm text-center`}
      >
        <span
          className={`${isDark ? "text-[#C3C9D3]" : "text-gray-500"
            } text-md font-medium`}
        >
          No sentiment data available for this user
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${isDark
        ? "bg-[#1A1C1F] border border-[#2E2F33]"
        : "bg-white border border-gray-200"
        } p-6 rounded-md shadow-sm`}
    >
      <div className="text-md font-bold text-center mb-4">
        {userName}'s Sentiment Analysis
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
        {/* OverAll Card */}
        <div
          className={`flex flex-col items-start justify-start rounded-lg shadow-sm p-6 px-20 min-h-[200px] ${isDark
            ? 'bg-gradient-to-br from-[#2A2B2E] to-[#34363A]'
            : 'bg-gradient-to-br from-[#FFF7E6] to-[#FFFFFF]'
            }`}
        // style={{
        //   backgroundColor:
        //     isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear-gradient(to bottom right, #FFF7E6, #ffffff)",
        // }}
        >
          <div className={`${isDark ? "text-[#C3C9D3]" : "text-gray-500"} text-sm font-light tracking-wide text-center w-full`}>
            OverAll
          </div>
          <div className="w-full border-b border-[#E5E7EB] mt-3 mb-4"></div>
          <div className="flex flex-col items-center w-full flex-1 justify-center">
            <div className="w-[90px] h-[90px] relative mb-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    innerRadius={32}
                    outerRadius={isDark ? 42 : 45}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {donutData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={donutColors[idx]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full border-t border-gray-200 my-1"></div>
            <div className="flex flex-row justify-center items-center w-full  gap-4">
              <span className="text-green-500 text-xs  font-semibold">
                Positive: {positiveCount}
              </span>
              <span className="text-red-500 text-xs font-semibold">
                Negative: {negativeCount}
              </span>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Total Messages: {totalMsgs}
            </div>
          </div>
        </div>
        {/* Percentage Card */}
        <div
          className={`flex flex-col items-start justify-start rounded-lg shadow-sm p-6 px-20 min-h-[200px] ${isDark
            ? 'bg-gradient-to-br from-[#2A2B2E] to-[#34363A]'
            : 'bg-gradient-to-br from-[#FFF7E6] to-[#FFFFFF]'
            }`}
        // style={{
        //   backgroundImage:
        //     isDark ? "linear-gradient(to-br, #2A2B2E, #34363A)" : "linear-gradient(to bottom right, #FFF7E6, #ffffff)",
        // }}
        >
          <div className={`${isDark ? "text-[#C3C9D3]" : "text-gray-500"} text-sm font-light tracking-wide text-center w-full`}>
            Percentage
          </div>
          <div className="w-full border-b border-[#E5E7EB] mb-4 mt-1"></div>
          <div className="flex flex-col items-center w-full flex-1 justify-center">
            <div className="w-full flex flex-col gap-2 mt-1">
              {/* Positive bar */}
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full justify-between mb-1">
                  <span className="flex items-center text-sm gap-1 text-green-500 font-medium">
                    Positive
                  </span>
                  <span className="text-green-500 text-sm font-bold">
                    {positivePercent}%
                  </span>
                </div>
                <div className="relative w-full h-3 bg-[#FAEDD0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#B59143] rounded-full transition-all duration-500"
                    style={{ width: `${positivePercent}%` }}
                  />
                </div>
              </div>
              {/* Negative bar */}
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full justify-between mb-1">
                  <span className="flex text-sm items-center gap-1 text-red-500 font-medium">
                    Negative
                  </span>
                  <span className="text-red-500 text-sm font-bold">
                    {negativePercent}%
                  </span>
                </div>
                <div className="relative w-full h-3 bg-[#FAEDD0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#B59143] rounded-full transition-all duration-500"
                    style={{ width: `${negativePercent}%` }}
                  />
                </div>
              </div>
              {/* Neutral bar */}
              <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full justify-between mb-1">
                  <span className="flex text-sm items-center gap-1 text-gray-500 font-medium">
                    Neutral
                  </span>
                  <span className="text-gray-500 text-sm font-bold">
                    {neutralPercent}%
                  </span>
                </div>
                <div className="relative w-full h-3 bg-[#FAEDD0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#B59143] rounded-full transition-all duration-500"
                    style={{ width: `${neutralPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Year/Month/Mode Selectors and Graph */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12 gap-4 min-h-[48px]">
        {/* Added Filter label */}
        <div className="flex items-center gap-2">
          <span
            className={`${isDark ? "text-[#C3C9D3]" : "text-gray-700"
              } w-[40px] text-sm font-medium`}
          >
            Filter :{" "}
          </span>
        </div>
        <div className="flex flex-row gap-4 w-full">
          {/* Mode Dropdown */}
          <div className="relative w-[150px]">
            <CustomDropdown
              value={mode}
              onChange={(value) => setMode(value as Mode)}
              isDark={isDark}
              className="w-full h-[32px] pr-6 px-3 py-1 text-sm font-normal rounded-md border-2 shadow-sm focus:border-[#B59143] focus:ring-0 focus:outline-none focus-visible:outline-none hover:border-[#B59143]"
              options={[
                { value: "today", label: "Today" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "yearly", label: "Yearly" }
              ]}
            />
          </div>
          {/* Month Dropdown */}
          {(mode === "weekly" || mode === "monthly") && (
            <div className="relative w-[150px]">
              <CustomDropdown
                value={month.toString()}
                onChange={(value) => setMonth(+value)}
                isDark={isDark}
                className="w-full h-[32px] pr-6 px-3 py-1 text-sm font-normal rounded-md border-2 shadow-sm focus:border-[#B59143] focus:ring-0 focus:outline-none focus-visible:outline-none hover:border-[#B59143]"
                options={MONTHS.map((mo) => ({ value: mo.value.toString(), label: mo.label }))}
              />
            </div>
          )}
          {/* Year Dropdown */}
          {mode !== "today" && mode !== "overall" && (
            <div className="relative w-[150px]">
              <CustomDropdown
                value={year.toString()}
                onChange={(value) => setYear(+value)}
                isDark={isDark}
                className="w-full h-[32px] pr-6 px-3 py-1 text-sm font-normal rounded-md border-2 shadow-sm focus:border-[#B59143] focus:ring-0 focus:outline-none focus-visible:outline-none hover:border-[#B59143]"
                options={YEARS.map((y) => ({ value: y.toString(), label: y.toString() }))}
              />
            </div>
          )}
        </div>
      </div>
      <div>
        <NetSentimentWave
          data={data}
          isChannelView={false}
          selectedUserId={selectedUser}
        />
      </div>
    </div>
  );
};

export default UserAnalysis;
